import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { Box, Typography, IconButton, Dialog } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import StopIcon from '@mui/icons-material/Stop';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { parseMidiFile } from '../utils/midiMarkerParser';
import { useMidiSync } from '../hooks/useMidiSync';
import { useMarkerSync } from '../hooks/useMarkerSync';
import { resolveAllNotesIndex, getNotesInMode, getDisplayRootNote } from '../utils/musicTheory';
import { resolveBackingScale } from '../utils/improScales';
import { markPlaybackActive, markPlaybackIdle } from '../utils/playbackActivity';
import { setNowPlaying, setNowPlayingState, setNowPlayingHandlers, clearNowPlaying } from '../utils/mediaSession';
import { ACCENT_COLOR } from '../config/uiTheme';
import { bindBackingMedia, createNativeMixBus } from '../utils/playbackMix';
import SectionTimeline from './SectionTimeline';
import { SELECTED_BG, SELECTED_FG } from '../config/uiTheme';

// Cache-bust par version de contenu : Storage sert les assets avec max-age=3600 a la
// MEME URL (le fichier est ecrase au meme path a chaque edition cote studio). Sans ca,
// le navigateur resservirait l'ancien MP3/MIDI jusqu'a 1h apres une mise a jour. updatedAt
// bouge a chaque upsert de la fiche -> l'URL change -> fetch frais ; inchange = cache garde.
const withCacheBust = (url, version) => {
  if (!url || !version) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`;
};

/**
 * BackingTrackPlayer - Compact player component for jam mode
 * Replaces NoteGrid when a backing track is active
 */
const BackingTrackPlayer = forwardRef(({
  track,
  artist,
  currentMode,
  onScaleChange,
  onClose,
  onPlaybackStart,
  onChordChange,
  onPlayingStateChange,
  onTempoChange,
  onTimeSignatureChange,
  onHint,
  initialJamScale,
  // Verrou controle par App (la pilule du header le regle aussi). Absent, le
  // player garde son propre verrou, arme par initialJamScale (page /video).
  // lockedScaleChecked : le verrou vient de la pilule et passe par la regle de
  // pause ; un choix du dialogue ci-dessous (liste de l'auteur) n'y passe jamais.
  lockedScaleType: controlledLockedScaleType,
  lockedScaleChecked = false,
  onLockedScaleTypeChange,
  onSectionChange,
  isModeUnlocked,
  // false = pas de lecture auto au chargement (page /video : le depart doit
  // etre declenche explicitement pour une capture propre). Defaut : true (App).
  autoPlay = true
}, ref) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferReady, setBufferReady] = useState(false);
  const [midiEvents, setMidiEvents] = useState(null);
  const [midiDuration, setMidiDuration] = useState(null);
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [loopedSection, setLoopedSection] = useState(null);
  const [showCompatible, setShowCompatible] = useState(false);
  // Type de gamme choisi par l'utilisateur : tant qu'il est actif, les marqueurs
  // de section continuent de piloter la ROOT (une piste qui module doit suivre
  // ses key centers) mais le TYPE affiche reste celui choisi (A penta -> F#
  // penta -> D penta). Le son, lui, ne change pas.
  const [ownLockedScaleType, setOwnLockedScaleType] = useState(initialJamScale || null);
  const isLockControlled = controlledLockedScaleType !== undefined;
  const lockedScaleType = isLockControlled ? controlledLockedScaleType : ownLockedScaleType;
  const setLockedScaleType = useCallback((type) => {
    if (isLockControlled) onLockedScaleTypeChange?.(type);
    else setOwnLockedScaleType(type);
  }, [isLockControlled, onLockedScaleTypeChange]);
  // Section en cours et sa gamme declaree. Un marqueur sans gamme herite de la
  // section precedente, jamais de currentMode : celui-ci vaut le type verrouille
  // des qu'il s'applique, la pause ne pourrait plus se detecter.
  const [sectionKey, setSectionKey] = useState(null);
  const sectionKeyRef = useRef(null);
  const resolvedScale = useMemo(() => resolveBackingScale({
    type: lockedScaleType,
    rootNote: sectionKey?.root || track.rootNote,
    mode: sectionKey?.scale || track.mode,
    isModeUnlocked,
    checked: isLockControlled && lockedScaleChecked,
  }), [lockedScaleType, sectionKey, track.rootNote, track.mode, isModeUnlocked, isLockControlled, lockedScaleChecked]);

  const scaleCycle = useMemo(
    () => [track.mode, ...(track.compatibleScales || []).filter((s) => s !== track.mode)],
    [track.mode, track.compatibleScales]
  );

  const isMonoTonal = !track.keyCenters || track.keyCenters.length <= 1;
  const keyRootsLabel = isMonoTonal
    ? track.rootNote
    : track.keyCenters.map(k => k.root).join(' · ');
  const hasCompatibleScales = track.compatibleScales && track.compatibleScales.length > 0;

  // Internal ref for audio element
  const internalAudioRef = useRef(null);
  const webAudioRef = useRef({ ctx: null, buffer: null, source: null, startedAt: 0, offset: 0 });
  const loopedSectionRef = useRef(null);
  // Boucle Web Audio du morceau ENTIER (repeat all). L'element <audio loop> laisse
  // un trou de 100-300 ms au raccord ; l'AudioBufferSourceNode boucle a l'echantillon
  // pres. loopedSectionRef porte alors {0, fin musicale} et ce flag distingue ce mode
  // de la boucle de section (loopedSection reste null).
  const isFullLoopRef = useRef(false);
  // Curseurs du suivi note/section pendant la lecture Web Audio. Des refs, pas des
  // locales d'effet : l'effet RAF est relance a chaque re-render d'App (callbacks
  // inline), et des locales remises a -1 re-declencheraient la section courante,
  // dont le meta.chord ecraserait la note cible affichee (flash puis retour).
  const waMidiIdxRef = useRef(-1);
  const waSectionIdxRef = useRef(-1);
  const waLastPosRef = useRef(-1);

  const audioRef = internalAudioRef;
  useEffect(() => bindBackingMedia(audioRef.current), [audioRef]);

  // Load MIDI sync file if available (unified parsing: notes + markers)
  useEffect(() => {
    if (!track.midiUrl) return;
    fetch(withCacheBust(track.midiUrl, track.updatedAt))
      .then(res => res.arrayBuffer())
      .then(buf => {
        const { noteEvents, sections: parsedSections, musicalDuration } = parseMidiFile(buf);
        setMidiEvents(noteEvents);
        setSections(parsedSections);
        setMidiDuration(musicalDuration || null);
      })
      .catch(err => console.warn('[BackingTrackPlayer] Failed to load MIDI sync:', err));
  }, [track.midiUrl, track.updatedAt]);

  // Pre-decode AudioBuffer for Web Audio looping
  useEffect(() => {
    if (!track.audioUrl) return;
    const wa = webAudioRef.current;
    if (!wa.ctx) wa.ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (!wa.mixBus) {
      wa.mixBus = createNativeMixBus('backing', wa.ctx);
      wa.mixBus.connect(wa.ctx.destination);
    }
    setBufferReady(false);
    let stale = false;
    fetch(withCacheBust(track.audioUrl, track.updatedAt))
      .then(res => res.arrayBuffer())
      .then(buf => wa.ctx.decodeAudioData(buf))
      .then(decoded => {
        if (stale) return;
        wa.buffer = decoded;
        setBufferReady(true);
      })
      .catch(err => console.warn('[BackingTrackPlayer] Failed to decode audio buffer:', err));
    return () => {
      stale = true;
      if (wa.source) { try { wa.source.stop(); } catch(e) {} wa.source = null; }
      isFullLoopRef.current = false;
      wa.mixBus?.dispose();
      wa.mixBus = null;
    };
  }, [track.audioUrl, track.updatedAt]);

  // Stable callback for MIDI sync — resolve enharmonic spelling to match the active scale.
  // Quand une gamme compatible est verrouillee, une fondamentale d'accord peut ne pas
  // lui appartenir : on retombe alors sur la gamme de base de la piste (ce qui sonne).
  const handleMidiChordChange = useCallback((note) => {
    if (!onChordChange) return;
    const noteIdx = resolveAllNotesIndex(note);
    const scaleNotes = getNotesInMode(track.rootNote, currentMode);
    let match = scaleNotes.find(n => resolveAllNotesIndex(n) === noteIdx);
    if (!match && currentMode !== track.mode) {
      match = getNotesInMode(track.rootNote, track.mode).find(n => resolveAllNotesIndex(n) === noteIdx);
    }
    onChordChange(match || note);
  }, [onChordChange, track.rootNote, track.mode, currentMode]);

  // Sync MIDI events to audio playback
  useMidiSync(audioRef.current, midiEvents, handleMidiChordChange);

  // Seule porte d'application du verrou : fin de section, choix dans le
  // dialogue, et App passe par la meme regle pour la pilule et l'armement.
  const applyTypeForSection = useCallback((type, key, checked) => {
    if (!onScaleChange) return;
    const { root, mode } = resolveBackingScale({
      type,
      rootNote: key?.root || track.rootNote,
      mode: key?.scale || track.mode,
      isModeUnlocked,
      checked,
    });
    onScaleChange(root, mode);
  }, [onScaleChange, track.rootNote, track.mode, isModeUnlocked]);

  // La gamme de base rend la main aux marqueurs
  const pickScaleType = useCallback((type) => {
    setLockedScaleType(type);
    applyTypeForSection(type, sectionKeyRef.current, false);
  }, [setLockedScaleType, applyTypeForSection]);

  const stepScaleType = useCallback((dir) => {
    if (scaleCycle.length < 2) return;
    const found = lockedScaleType ? scaleCycle.indexOf(lockedScaleType) : 0;
    const index = found === -1 ? 0 : found;
    const next = scaleCycle[(index + dir + scaleCycle.length) % scaleCycle.length];
    pickScaleType(next === track.mode ? null : next);
  }, [scaleCycle, lockedScaleType, pickScaleType, track.mode]);

  // Handle section changes from markers
  const handleSectionChange = useCallback((section) => {
    setActiveSection(section);
    const { meta } = section;
    const previous = sectionKeyRef.current;
    const hasKey = !!(meta.root || meta.scale);
    const key = hasKey
      ? { name: section.name, root: meta.root || track.rootNote, scale: meta.scale || previous?.scale || track.mode }
      : previous && { ...previous, name: section.name };
    if (key) {
      sectionKeyRef.current = key;
      setSectionKey(key);
      if (onSectionChange) onSectionChange(key);
    }
    if (hasKey) applyTypeForSection(lockedScaleType, key, isLockControlled && lockedScaleChecked);
    // meta.chord is a chord *symbol* (e.g. "Em"); activeChordNote everywhere
    // else is a bare note name. Strip the quality and route the root through
    // the same scale-aware resolution as the MIDI note events, so the first
    // measure of a section highlights the chord tones instead of just the tonic.
    if (meta.chord) {
      const chordRoot = (meta.chord.match(/^[A-G][#b]?/) || [])[0];
      if (chordRoot) handleMidiChordChange(chordRoot);
    }
    if (meta.tempo && onTempoChange) onTempoChange(meta.tempo);
    if (meta.ts && onTimeSignatureChange) onTimeSignatureChange(meta.ts);
    if (meta.hint && onHint) onHint(meta.hint);
  }, [applyTypeForSection, onSectionChange, onTempoChange, onTimeSignatureChange, onHint, handleMidiChordChange, track.rootNote, track.mode, lockedScaleType, isLockControlled, lockedScaleChecked]);

  // Sync marker sections to audio playback
  useMarkerSync(audioRef.current, sections, handleSectionChange);

  // Version ref pour la boucle RAF Web Audio : evite de relancer l'effet a chaque
  // re-render d'App (onScaleChange est une inline arrow, identite instable)
  const handleSectionChangeRef = useRef(handleSectionChange);
  useEffect(() => { handleSectionChangeRef.current = handleSectionChange; }, [handleSectionChange]);

  // Le verrou est un choix par piste : il tombe au changement de backing, ou se
  // pre-arme avec le rung "Jam it your way" choisi sur la carte. En mode
  // controle, c'est App qui le remet a chaque entree en jam.
  useEffect(() => {
    setOwnLockedScaleType(initialJamScale || null);
  }, [track.id, initialJamScale]);

  useEffect(() => {
    sectionKeyRef.current = null;
    setSectionKey(null);
    setActiveSection(null);
  }, [track.id]);

  // Ne pas laisser le flag d'activite coince si le player est demonte en pleine lecture
  useEffect(() => () => markPlaybackIdle('backingTrack'), []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      if (!autoPlay) return;
      if (onPlaybackStart) onPlaybackStart();
      audio.play().catch(() => {});
    };

    const handleEnded = () => {
      // Le morceau boucle toujours : filet si l'element audio atteint la fin
      // avant que la boucle gapless (Web Audio ou audio.loop) ait pris le relais
      audio.currentTime = 0;
      audio.play().catch(() => {});
    };

    const handlePlay = () => {
      setIsPlaying(true);
      markPlaybackActive('backingTrack');
      if (onPlayingStateChange) onPlayingStateChange(true);
      document.dispatchEvent(new CustomEvent('backingtrack:play', { detail: { trackId: track.id } }));
    };

    const handlePause = () => {
      // Don't flip to paused if a WebAudio loop is active (section playing)
      if (webAudioRef.current.source) return;
      setIsPlaying(false);
      markPlaybackIdle('backingTrack');
      if (onPlayingStateChange) onPlayingStateChange(false);
    };

    let rafId;
    // setCurrentTime re-renders the slider + timeline; 5 Hz is enough visually,
    // 60 fps would re-render them on every frame for the whole track.
    let lastTimeUpdate = 0;
    const TIME_UPDATE_MS = 200;
    const throttledSetCurrentTime = (pos) => {
      const now = performance.now();
      if (now - lastTimeUpdate >= TIME_UPDATE_MS) {
        lastTimeUpdate = now;
        setCurrentTime(pos);
      }
    };
    const tick = () => {
      const wa = webAudioRef.current;
      const currentLoop = loopedSectionRef.current;
      if (wa.source && currentLoop) {
        const elapsed = wa.ctx.currentTime - wa.startedAt;
        const loopLen = currentLoop.endTime - currentLoop.startTime;
        const pos = currentLoop.startTime + (elapsed % loopLen);
        throttledSetCurrentTime(pos);
        if (pos < waLastPosRef.current) { waMidiIdxRef.current = -1; waSectionIdxRef.current = -1; }
        waLastPosRef.current = pos;
        if (midiEvents && midiEvents.length > 0) {
          let idx = -1;
          let lo = 0, hi = midiEvents.length - 1;
          while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (midiEvents[mid].time <= pos) { idx = mid; lo = mid + 1; }
            else hi = mid - 1;
          }
          if (idx !== waMidiIdxRef.current && idx >= 0) {
            waMidiIdxRef.current = idx;
            handleMidiChordChange(midiEvents[idx].note);
          }
        }
        // Boucle du morceau entier : les marqueurs de section doivent continuer
        // a piloter gamme/hint (useMarkerSync est muet, l'element est en pause)
        if (isFullLoopRef.current && sections && sections.length > 0) {
          let idx = -1;
          let lo = 0, hi = sections.length - 1;
          while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (sections[mid].startTime <= pos) { idx = mid; lo = mid + 1; }
            else hi = mid - 1;
          }
          if (idx !== waSectionIdxRef.current && idx >= 0) {
            waSectionIdxRef.current = idx;
            handleSectionChangeRef.current(sections[idx]);
          }
        }
      } else if (!audio.paused) {
        throttledSetCurrentTime(audio.currentTime);
      } else {
        return;
      }
      rafId = requestAnimationFrame(tick);
    };
    const startRaf = () => { rafId = requestAnimationFrame(tick); };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('play', startRaf);
    audio.addEventListener('pause', handlePause);
    if (webAudioRef.current.source) startRaf();
    else if (!audio.paused) startRaf();

    return () => {
      cancelAnimationFrame(rafId);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('play', startRaf);
      audio.removeEventListener('pause', handlePause);
    };
  }, [loopedSection, isPlaying, midiEvents, sections, handleMidiChordChange, autoPlay]);

  // Loop de l'element audio = filet de secours tant que le buffer Web Audio
  // n'est pas decode (des qu'il l'est, la boucle gapless prend le relais)
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = !loopedSection && !bufferReady;
    }
  }, [loopedSection, bufferReady]);

  useEffect(() => {
    const handlePlayEvent = (e) => {
      if (e.detail?.trackId === track.id) return;
      const audio = audioRef.current;
      if (audio && !audio.paused) audio.pause();
      const wa = webAudioRef.current;
      if (wa.source) {
        try { wa.source.stop(); } catch (err) {}
        wa.source = null;
        loopedSectionRef.current = null;
        isFullLoopRef.current = false;
        setLoopedSection(null);
        setIsPlaying(false);
        if (onPlayingStateChange) onPlayingStateChange(false);
      }
    };
    document.addEventListener('backingtrack:play', handlePlayEvent);
    return () => document.removeEventListener('backingtrack:play', handlePlayEvent);
  }, [track.id, onPlayingStateChange]);

  const togglePlay = () => {
    const wa = webAudioRef.current;
    const audio = audioRef.current;
    if (wa.source) {
      // Pause d'une boucle Web Audio (section ou morceau entier)
      const pos = getWaPosition();
      const wasFullLoop = isFullLoopRef.current;
      stopWebAudioLoop();
      if (wasFullLoop && pos != null) {
        isFullLoopRef.current = false;
        loopedSectionRef.current = null;
        setCurrentTime(pos);
        if (audio) audio.currentTime = pos;
      }
      setIsPlaying(false);
      markPlaybackIdle('backingTrack');
      if (onPlayingStateChange) onPlayingStateChange(false);
      return;
    }
    if (loopedSection) {
      startWebAudioLoop(loopedSection);
      setIsPlaying(true);
      if (onPlayingStateChange) onPlayingStateChange(true);
      return;
    }
    if (bufferReady) {
      if (onPlaybackStart) onPlaybackStart();
      if (startFullLoop(currentTime)) {
        setIsPlaying(true);
        if (onPlayingStateChange) onPlayingStateChange(true);
        return;
      }
    }
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      if (onPlaybackStart) onPlaybackStart();
      audio.play();
    }
  };

  // Stop : coupe le son, annule toute boucle de section et revient au debut du morceau.
  // C'est le seul vrai "arret" (le play/pause ne fait que figer/reprendre).
  const handleStop = () => {
    stopWebAudioLoop();
    isFullLoopRef.current = false;
    loopedSectionRef.current = null;
    setLoopedSection(null);
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setCurrentTime(0);
    setIsPlaying(false);
    markPlaybackIdle('backingTrack');
    if (onPlayingStateChange) onPlayingStateChange(false);
  };

  // Fiche du lecteur systeme (ecran verrouille, centre de controle, CarPlay)
  useEffect(() => {
    setNowPlaying({ title: track.title, artist: artist?.name, artwork: artist?.avatar });
  }, [track.title, artist?.name, artist?.avatar]);

  useEffect(() => {
    setNowPlayingState(isPlaying);
  }, [isPlaying]);

  // Ref pour les boutons systeme : togglePlay est recree a chaque render, les
  // handlers, eux, ne sont poses qu'une fois (les reposer a chaque render
  // reveillerait le lecteur systeme sans raison).
  const transportRef = useRef(null);
  useEffect(() => {
    transportRef.current = { togglePlay, handleStop, isPlaying: isPlaying || !!webAudioRef.current.source };
  });
  const scaleShortcutRef = useRef(null);
  useEffect(() => {
    scaleShortcutRef.current = { enabled: hasCompatibleScales, step: stepScaleType };
  });
  useEffect(() => {
    setNowPlayingHandlers({
      play: () => { if (!transportRef.current.isPlaying) transportRef.current.togglePlay(); },
      pause: () => { if (transportRef.current.isPlaying) transportRef.current.togglePlay(); },
      stop: () => transportRef.current.handleStop(),
    });
    return () => clearNowPlaying();
  }, []);

  const rootRef = useRef(null);
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.repeat) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const isSpace = e.code === 'Space';
      const key = typeof e.key === 'string' ? e.key.toLowerCase() : '';
      const isScaleKey = key === 'c' || key === 'x';
      if (!isSpace && !isScaleKey) return;
      const target = e.target;
      if (target?.closest?.('input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="slider"]')) return;
      const isElement = typeof Element !== 'undefined' && target instanceof Element;
      const insidePlayer = isElement && !!rootRef.current?.contains(target);
      const isBackground = !isElement || target === document.body || target === document.documentElement;
      if (!insidePlayer && !isBackground) return;
      if (isScaleKey && !scaleShortcutRef.current?.enabled) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (isSpace) transportRef.current?.togglePlay();
      else scaleShortcutRef.current?.step(key === 'c' ? 1 : -1);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const stopWebAudioLoop = useCallback(() => {
    const wa = webAudioRef.current;
    if (wa.source) {
      try { wa.source.stop(); } catch(e) {}
      wa.source = null;
    }
  }, []);

  const startWebAudioLoop = useCallback((section, offset) => {
    const wa = webAudioRef.current;
    if (!wa.ctx || !wa.buffer) return false;
    stopWebAudioLoop();
    if (wa.ctx.state === 'suspended') wa.ctx.resume();
    const startAt = (offset != null && offset >= section.startTime && offset < section.endTime)
      ? offset
      : section.startTime;
    const src = wa.ctx.createBufferSource();
    src.buffer = wa.buffer;
    src.loop = true;
    src.loopStart = section.startTime;
    src.loopEnd = section.endTime;
    src.connect(wa.mixBus);
    src.start(0, startAt);
    wa.source = src;
    // startedAt recule de l'offset deja consomme : tick() retrouve la vraie position
    wa.startedAt = wa.ctx.currentTime - (startAt - section.startTime);
    wa.offset = section.startTime;
    waMidiIdxRef.current = -1;
    waSectionIdxRef.current = -1;
    waLastPosRef.current = -1;
    markPlaybackActive('backingTrack');
    document.dispatchEvent(new CustomEvent('backingtrack:play', { detail: { trackId: track.id } }));
    return true;
  }, [stopWebAudioLoop, track.id]);

  // Position de lecture courante de la boucle Web Audio (section ou morceau entier)
  const getWaPosition = useCallback(() => {
    const wa = webAudioRef.current;
    const loop = loopedSectionRef.current;
    if (!wa.source || !loop) return null;
    const len = loop.endTime - loop.startTime;
    return loop.startTime + ((wa.ctx.currentTime - wa.startedAt) % len);
  }, []);

  // Boucle gapless du morceau entier. Point de bouclage = duree musicale du MIDI
  // (EOT) quand elle colle a l'audio ; sinon la fin du buffer decode. Les deux
  // battent le loop de l'element <audio> de plusieurs ordres de grandeur.
  const startFullLoop = useCallback((offset = 0) => {
    const wa = webAudioRef.current;
    if (!wa.ctx || !wa.buffer) return false;
    const bufEnd = wa.buffer.duration;
    const loopEnd = (midiDuration && Math.abs(midiDuration - bufEnd) < 0.5)
      ? Math.min(midiDuration, bufEnd)
      : bufEnd;
    const section = { startTime: 0, endTime: loopEnd };
    loopedSectionRef.current = section;
    isFullLoopRef.current = true;
    const ok = startWebAudioLoop(section, Math.min(Math.max(offset, 0), loopEnd - 0.01));
    if (!ok) {
      loopedSectionRef.current = null;
      isFullLoopRef.current = false;
    }
    return ok;
  }, [midiDuration, startWebAudioLoop]);

  // Ref exposee au parent : pause universelle (App), plus togglePlay/restart
  // pour les hotes sans transport visible (page /video). Pas de tableau de deps :
  // togglePlay est recree a chaque render, le handle doit suivre.
  useImperativeHandle(ref, () => ({
    pause: () => {
      const pos = getWaPosition();
      stopWebAudioLoop();
      isFullLoopRef.current = false;
      loopedSectionRef.current = null;
      setLoopedSection(null);
      const audio = internalAudioRef.current;
      if (pos != null) {
        setCurrentTime(pos);
        if (audio) audio.currentTime = pos;
      }
      if (audio && !audio.paused) {
        audio.pause();
      } else {
        setIsPlaying(false);
        markPlaybackIdle('backingTrack');
        if (onPlayingStateChange) onPlayingStateChange(false);
      }
    },
    togglePlay: () => togglePlay(),
    isPlaying: () => isPlaying || !!webAudioRef.current.source,
    restart: () => {
      stopWebAudioLoop();
      isFullLoopRef.current = false;
      loopedSectionRef.current = null;
      setLoopedSection(null);
      const audio = internalAudioRef.current;
      if (audio) audio.currentTime = 0;
      setCurrentTime(0);
      if (bufferReady && startFullLoop(0)) {
        setIsPlaying(true);
        if (onPlayingStateChange) onPlayingStateChange(true);
      } else if (audio) {
        audio.play().catch(() => {});
      }
    },
  }));

  // Des que le buffer est decode, la lecture "repeat all" bascule de l'element
  // audio vers la boucle Web Audio (demarre le webaudio AVANT de mettre l'element
  // en pause : handlePause voit wa.source et ne coupe pas l'etat isPlaying).
  useEffect(() => {
    if (!bufferReady || loopedSection) return;
    const audio = audioRef.current;
    const wa = webAudioRef.current;
    if (!audio || audio.paused || wa.source) return;
    const pos = audio.currentTime;
    if (startFullLoop(pos)) audio.pause();
  }, [bufferReady, loopedSection, isPlaying, startFullLoop, audioRef]);

  const handleSectionTap = (section) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Couper les autres sons AVANT de demarrer la boucle : handleStopAllSounds
    // (App) rappelle pause() de CE player, ce qui tuerait une boucle deja lancee
    if (onPlaybackStart) onPlaybackStart();

    audio.currentTime = section.startTime;
    setCurrentTime(section.startTime);
    handleSectionChange(section);

    const isDeselect = loopedSection && loopedSection.startTime === section.startTime;
    if (isDeselect) {
      // Re-tap sur la section qui boucle : on quitte la boucle et on reprend
      // la lecture du morceau entier (boucle gapless si le buffer est pret)
      stopWebAudioLoop();
      loopedSectionRef.current = null;
      setLoopedSection(null);
      if (!startFullLoop(section.startTime)) {
        if (audio.paused) audio.play();
      } else {
        setIsPlaying(true);
        if (onPlayingStateChange) onPlayingStateChange(true);
      }
    } else {
      // Tap une section : on la met en boucle (bascule si une autre bouclait deja)
      audio.pause();
      isFullLoopRef.current = false;
      loopedSectionRef.current = section;
      if (startWebAudioLoop(section)) {
        setLoopedSection(section);
        setIsPlaying(true);
        if (onPlayingStateChange) onPlayingStateChange(true);
      } else {
        loopedSectionRef.current = null;
        audio.play();
      }
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const accentColor = ACCENT_COLOR;

  return (
    <Box ref={rootRef} sx={{
      backgroundColor: 'rgba(30, 30, 40, 0.8)',
      borderRadius: '20px',
      overflow: 'hidden',
      mx: 2,
      mb: 2
    }}>
      {/* Hidden audio element */}
      <audio ref={audioRef} crossOrigin="anonymous" src={track.audioUrl} preload="metadata" />

      {/* Header row — in landscape, controls move here */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        pb: 1,
        gap: 1
      }}>
        {/* Back button */}
        <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', ml: -1 }}>
          <ChevronLeftIcon />
        </IconButton>

        {/* Artist avatar */}
        {artist?.avatar && (
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundImage: `url(${artist.avatar})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        )}

        {/* Track info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '1rem',
            fontWeight: 'bold',
            color: 'white',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {track.title}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
            {activeSection ? `${activeSection.name} · ` : ''}
            {lockedScaleType && !resolvedScale.paused ? (
              <Box component="span" sx={{ color: '#fff', fontWeight: 'bold' }}>
                {getDisplayRootNote(resolvedScale.root, resolvedScale.mode)} {resolvedScale.mode}
              </Box>
            ) : (
              `${getDisplayRootNote(resolvedScale.root, resolvedScale.mode)} ${resolvedScale.mode}`
            )}
            {artist && ` · ${artist.name}`}
          </Typography>
        </Box>

        {/* Controls inline — landscape only */}
        <Box sx={{
          display: 'none',
          '@media (orientation: landscape)': {
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }
        }}>
          {/* Time display */}
          <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap' }}>
            {formatTime(currentTime)} / {formatTime(duration) || track.duration}
          </Typography>

          {/* Play/Pause button */}
          <Box
            onClick={togglePlay}
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              backgroundColor: SELECTED_BG,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'scale(1.05)' },
              '&:active': { transform: 'scale(0.95)' }
            }}
          >
            {isPlaying ? (
              <PauseIcon sx={{ fontSize: 22, color: SELECTED_FG }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: 22, color: SELECTED_FG, ml: 0.3 }} />
            )}
          </Box>

          {/* Stop button */}
          <Box
            onClick={handleStop}
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'scale(1.05)', backgroundColor: 'rgba(255,255,255,0.16)' },
              '&:active': { transform: 'scale(0.95)' }
            }}
          >
            <StopIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.85)' }} />
          </Box>

          {/* Compatible scales button */}
          {hasCompatibleScales && (
            <Box
              onClick={() => setShowCompatible(true)}
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                backgroundColor: SELECTED_BG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'scale(1.05)' },
                '&:active': { transform: 'scale(0.95)' }
              }}
            >
              <MusicNoteIcon sx={{ fontSize: 20, color: SELECTED_FG }} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Section Timeline */}
      {sections.length > 0 && (
        <SectionTimeline
          sections={sections}
          currentTime={currentTime}
          duration={duration}
          activeSection={activeSection}
          loopedSection={loopedSection}
          onSectionTap={handleSectionTap}
          accentColor={accentColor}
        />
      )}

      {/* Controls row — portrait only */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        pb: 2,
        '@media (orientation: landscape)': {
          display: 'none'
        }
      }}>
        {/* Time display */}
        <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', minWidth: 90 }}>
          {formatTime(currentTime)} / {formatTime(duration) || track.duration}
        </Typography>

        {/* Center controls: play + compatible scales */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Play/Pause button */}
          <Box
            onClick={togglePlay}
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: SELECTED_BG,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)'
              },
              '&:active': {
                transform: 'scale(0.95)'
              }
            }}
          >
            {isPlaying ? (
              <PauseIcon sx={{ fontSize: 28, color: SELECTED_FG }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: 28, color: SELECTED_FG, ml: 0.3 }} />
            )}
          </Box>

          {/* Stop button */}
          <Box
            onClick={handleStop}
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'scale(1.05)', backgroundColor: 'rgba(255,255,255,0.16)' },
              '&:active': { transform: 'scale(0.95)' }
            }}
          >
            <StopIcon sx={{ fontSize: 26, color: 'rgba(255,255,255,0.85)' }} />
          </Box>

          {/* Compatible scales button */}
          {hasCompatibleScales && (
            <Box
              onClick={() => setShowCompatible(true)}
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: SELECTED_BG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'scale(1.05)' },
                '&:active': { transform: 'scale(0.95)' }
              }}
            >
              <MusicNoteIcon sx={{ fontSize: 26, color: SELECTED_FG }} />
            </Box>
          )}
        </Box>

        {/* Spacer to keep center controls centered */}
        <Box sx={{ minWidth: 90 }} />
      </Box>

      {/* Compatible Scales Dialog */}
      <Dialog
        open={showCompatible}
        onClose={() => setShowCompatible(false)}
        PaperProps={{
          sx: {
            bgcolor: '#15151A',
            backgroundImage: 'none',
            borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.08)',
            maxWidth: 340,
            mx: 2
          }
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'white', mb: 0.5 }}>
            Compatible Scales
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', mb: 2 }}>
            {isMonoTonal
              ? `${track.rootNote} ${track.mode}`
              : `Follows the key centers (${keyRootsLabel})`}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {scaleCycle.map((scale) => {
              const isBase = scale === track.mode;
              const isCurrent = isBase ? !lockedScaleType : scale === lockedScaleType;
              return (
                <Box
                  key={scale}
                  onClick={() => {
                    if (!isCurrent) pickScaleType(isBase ? null : scale);
                    setShowCompatible(false);
                  }}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '8px',
                    cursor: isCurrent ? 'default' : 'pointer',
                    backgroundColor: isCurrent ? SELECTED_BG : 'rgba(255,255,255,0.08)',
                    transition: 'all 0.15s ease',
                    ...(!isCurrent && {
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' }
                    })
                  }}
                >
                  <Typography sx={{
                    fontSize: '0.85rem',
                    color: isCurrent ? SELECTED_FG : 'rgba(255,255,255,0.8)',
                    fontWeight: isCurrent ? 600 : 'normal'
                  }}>
                    {isMonoTonal ? `${track.rootNote} ${scale}` : scale}
                    {isBase && ' (track)'}
                    {isCurrent && (!isBase && resolvedScale.paused ? ' (paused)' : ' (current)')}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
});

BackingTrackPlayer.displayName = 'BackingTrackPlayer';

export default BackingTrackPlayer;
